import { isEqual } from 'lodash';
import QRCodeStyling, { Options } from 'qr-code-styling';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

export interface UseQRCodeReturn {
    download: (filename: string) => void;
}

const useQRCodeStyling = (options: Options): QRCodeStyling | null => {
    // Ensure this runs only on the client side
    if (typeof window !== 'undefined') {
        const QRCodeStylingLib = require('qr-code-styling');
        return new QRCodeStylingLib(options);
    }
    return null;
};

const defaultOptions: Options = {
    width: 1028,
    height: 1028,
    type: 'svg',
    shape: 'square',
    image: '/app_logo.webp',
    imageOptions: {
        crossOrigin: 'anonymous',
        margin: 12,
        imageSize: 0.3
    },
    dotsOptions: {
        color: '#11181C',
        type: 'rounded'
    },
    cornersSquareOptions: {
        type: 'extra-rounded'
    },
    backgroundOptions: {
        color: '#ffffff'
    },
    qrOptions: {
        errorCorrectionLevel: 'H',
        typeNumber: 0 // Auto-detect best version
    }
};

const useQRCode = (
    text: string,
    containerRef?: RefObject<HTMLElement | null>,
    options?: Partial<Options>
): UseQRCodeReturn => {
    const qrCodeRef = useRef<QRCodeStyling | null>(null);
    const qrOptions = useMemo(
        () => ({
            ...defaultOptions,
            ...options,
            data: text
        }),
        [text, options]
    );
    const lastQrCodeOptions = useRef<Partial<Options>>(qrOptions);

    const initQRCode = useCallback(() => {
        if (qrCodeRef.current && isEqual(lastQrCodeOptions.current, qrOptions)) return;
        lastQrCodeOptions.current = qrOptions;

        if (typeof window !== 'undefined') {
            const QRCodeStylingLib = require('qr-code-styling');
            qrCodeRef.current = new QRCodeStylingLib(qrOptions);
        }
    }, [qrOptions]);

    const download = useCallback((filename: string) => {
        initQRCode();
        qrCodeRef.current?.download({ name: filename, extension: 'png' });
    }, []);

    useEffect(() => {
        if (!containerRef?.current) return;
        initQRCode();

        containerRef.current.childNodes.forEach((node) => node.remove());
        qrCodeRef.current.append(containerRef.current);
    }, [qrOptions]);

    return { download };
};

export default useQRCode;
